import { useEffect, useState } from 'react';
import UserDataService from '../../../services/user-data.service';
import { AppUser, UserListInput } from '../../../types/user.types';
import { map, debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { getLocateDate } from '../../../utils/format-date-time.utils';
import DateRangeFilter, { DateRangeType } from '../../common/DateRangeFilter';
import { TextField } from '../../common/InputField';

const Users = () => {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [totalUserCount, setTotalUserCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [dates, setDates] = useState<DateRangeType>({
    startDate: undefined,
    endDate: undefined,
  });
  const navigate = useNavigate();
  const limit = 20;

  const getUserList = async (isFetchMore?: boolean, search?: string) => {
    try {
      setLoading(true);
      const input: UserListInput = {
        limit,
        offset: isFetchMore ? users.length : 0,
        startDate: dates.startDate ?? dates.startDate,
        endDate: dates.endDate ?? dates.endDate,
        searchText: search || '',
      };
      const response = await UserDataService.getAppUsers(input);
      if (response.data.data && response.data.data.users) {
        if (!isFetchMore) {
          setTotalUserCount(response.data.data.totalUsers);
          setUsers(response.data.data.users);
        } else {
          setUsers([...users, ...response.data.data.users]);
        }
      } else {
        console.log('Error: ', response.data.message);
      }
      setLoading(false);
    } catch (err) {
      console.log('Error: ', err);
      setLoading(false);
    }
  };

  const debouncedSearch = debounce((val: string) => {
    if (val.length >= 3 || val.length === 0) {
      getUserList(false, val);
    }
  }, 400);

  const handleScroll = (e: any) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (
      scrollTop + clientHeight + 50 >= scrollHeight &&
      !loading &&
      users.length < totalUserCount
    ) {
      getUserList(true, searchText);
    }
  };

  useEffect(() => {
    getUserList();
  }, []);

  useEffect(() => {
    debouncedSearch(searchText);
    return () => debouncedSearch.cancel();
  }, [searchText]);

  useEffect(() => {
    setSearchText('');
    getUserList();
  }, [dates]);

  return (
    <div className="overflow-y-auto w-full p-5">
      <div className="flex justify-between items-center mb-8 z-[2] relative">
        <h3 className="font-normal text-[40px]">Users</h3>
        <div className="flex">
          <div className="mx-4 w-[300px]">
            <TextField
              name="search"
              placeholder="Search by name or email"
              className="w-[170px]"
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
            />
          </div>
          <DateRangeFilter
            setDateRange={(values) => setDates(values)}
            dateRange={dates}
          />
        </div>
      </div>

      <div
        className="overflow-y-auto max-h-[calc(110vh-200px)] border border-[#ddd] px-4"
        onScroll={handleScroll}
      >
        <table className="text-[14px] bg-white min-w-full">
          <thead>
            <tr className="text-[#616161]">
              {[
                'Name',
                'Plan',
                'Email',
                'Data Retention',
                'Token Input',
                'Token Output',
                'Date Joined',
                '',
              ].map((header, idx) => (
                <th
                  key={idx}
                  className="p-3 min-w-[100px] sticky top-0 bg-[white] z-[1]"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-[#333] font-regular">
            {users &&
              users.length > 0 &&
              map(users, (user) => (
                <tr className="static rounded-lg border-b-8 border-white last:border-none">
                  <td className="px-3 py-5 first:font-semibold bg-[#fcfcfc]">{`${user.firstName} ${user.lastName}`}</td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">
                    {user.plan?.product?.name}
                  </td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">{user.email}</td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">
                    {user?.aiDataRetention ? 'Yes' : 'No'}
                  </td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">
                    {user.totalUsedInputTokens}
                  </td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">
                    {user.totalUsedOutputTokens}
                  </td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">
                    {user?.createdAt &&
                      getLocateDate(user?.createdAt?.toString())}
                  </td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">
                    <span
                      className="font-bold cursor-pointer"
                      onClick={() =>
                        navigate(`/users/${user._id}`, {
                          state: {
                            name: `${user.firstName} ${user?.lastName}`,
                          },
                        })
                      }
                    >
                      View Details
                    </span>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Users;
